import React, { forwardRef, useCallback } from "react";

import { truncateInvestmentTag, getTagInitials } from "Helpers/index";
import Tooltip from "frontend/src/components/Tooltip/Tooltip";

interface FundTagProps {
  extraClassName?: string;
  truncate?: boolean;
  truncatingLength?: number;
  title: string;
  truncateWithInitials?: boolean;
}

interface FundTagRef {
  node?: HTMLElement;
}

export const FundTag = forwardRef<FundTagRef, FundTagProps>(
  (
    {
      extraClassName = "",
      truncate = true,
      truncatingLength = 13,
      title,
      truncateWithInitials = false,
    },
    ref
  ) => {
    const truncateTitle = useCallback((): string => {
      if (truncateWithInitials) {
        return getTagInitials(title);
      }
      return truncateInvestmentTag(title, truncatingLength);
    }, [truncateWithInitials, title, truncatingLength]);

    const isSuitableForTruncation = useCallback((): boolean => {
      return title.length > truncatingLength;
    }, [title, truncatingLength]);

    const handleRef = useCallback(
      (node: HTMLDivElement | null) => {
        if (ref && typeof ref === "object") {
          ref.current = { node: node || undefined };
        }
      },
      [ref]
    );

    const shouldShowTooltip = truncate && (isSuitableForTruncation() || truncateWithInitials);

    const tagElement = (
      <div
        className={`ant-tag ant-tag--fund ${extraClassName}`}
        ref={handleRef}
      >
        {shouldShowTooltip ? truncateTitle() : title}
      </div>
    );

    if (shouldShowTooltip) {
      return (
        <Tooltip
          title={title}
          placement="top"
          overlayClassName="fund-tag-tooltip"
          align={{ useCssBottom: true }}
          overlayStyle={{ position: "fixed" }}
          destroyTooltipOnHide
        >
          {tagElement}
        </Tooltip>
      );
    }

    return tagElement;
  }
);

FundTag.displayName = "FundTag";
